<template>
  <div>
    <div class="header">
      <h1>订单开票</h1>
      <div class="user-info">
        <span>欢迎，用户</span>
        <button @click="logout">退出登录</button>
      </div>
    </div>
    
    <div class="container">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
        <h2 class="section-title">{{ isInvoiceRecords ? '开票记录' : '全部开票订单' }}</h2>
        <button class="btn btn-primary" @click="toggleView">{{ isInvoiceRecords ? '返回订单列表' : '开票记录' }}</button>
      </div>
      
      <div class="order-list" v-if="!isInvoiceRecords">
        <div v-if="filteredOrders.length === 0" class="empty-state">
          
          <h3>您暂无可开票订单</h3>
          <p>您目前没有需要开票的订单</p>
        </div>
        <div v-else class="order-card" v-for="order in filteredOrders" :key="order.id">
          <div class="order-header">
            <div class="order-header-left">
              <input v-if="order.status === '未开票'" type="checkbox" class="order-checkbox" :data-order-id="order.id" @change="updateFloatingButton">
              <span v-else class="checkbox-placeholder"></span>
              <span class="order-id">订单号：{{ order.orderId }}</span>
            </div>
            <span class="order-status" :class="getStatusClass(order.status)">{{ order.status }}</span>
          </div>
          <div class="order-body">
            <div class="order-item">
              <span class="value" style="font-weight: 500;">商品名称：{{ order.product }}</span>
              <span class="value" style="color: #ff4d4f; font-weight: 600;">¥{{ order.amount.toFixed(2) }}</span>
            </div>
            <div class="order-item">
              <span class="value">角色名称：{{ order.customer }}</span>
              <span class="value">数量：{{ order.quantity }}</span>
            </div>
            <div class="order-item">
              <span class="value">下单日期：{{ order.date }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div class="order-list" v-else>
        <!-- 加载中状态 -->
        <div v-if="isLoading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>加载中...</p>
        </div>
        
        <!-- 空状态 -->
        <div v-else-if="filteredInvoiceRecords.length === 0" class="empty-state">
          <h3>暂无开票记录</h3>
          <p>您目前没有开票记录</p>
        </div>
        
        <!-- 开票记录列表 -->
        <div v-else class="invoice-record-card" v-for="record in filteredInvoiceRecords" :key="record.id">
          <div class="invoice-record-header">
            <span class="invoice-record-title">{{ record.title }}</span>
            <span class="invoice-record-status" :class="getInvoiceStatusClass(record.status)">{{ record.status }}</span>
          </div>
          <div class="invoice-record-body">
            <div class="invoice-record-item">
              <span class="label">发票金额：</span>
              <span class="value" style="color: #ff4d4f; font-weight: 600;">¥{{ record.amount.toFixed(2) }}</span>
            </div>
            <div class="invoice-record-item">
              <span class="label">申请时间：</span>
              <span class="value">{{ record.applyTime }}</span>
            </div>
            <div class="invoice-record-item">
              <span class="label">发票类型：</span>
              <span class="value">{{ record.type }}</span>
            </div>
            <div class="invoice-record-item" v-if="record.orderIds">
              <span class="label">订单号：</span>
              <span class="value">{{ record.orderIds }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 分页控件 -->
      <div class="pagination" v-if="(isInvoiceRecords ? totalInvoicePages : totalPages) > 1">
        <div class="pagination-controls">
          <button class="pagination-btn" @click="changePage((isInvoiceRecords ? currentInvoicePage : currentPage) - 1)" :disabled="isInvoiceRecords ? currentInvoicePage === 1 : currentPage === 1">上一页</button>
          <button v-for="i in (isInvoiceRecords ? totalInvoicePages : totalPages)" :key="i" class="pagination-btn" :class="{ active: i === (isInvoiceRecords ? currentInvoicePage : currentPage) }" @click="changePage(i)">{{ i }}</button>
          <button class="pagination-btn" @click="changePage((isInvoiceRecords ? currentInvoicePage : currentPage) + 1)" :disabled="isInvoiceRecords ? currentInvoicePage === totalInvoicePages : currentPage === totalPages">下一页</button>
        </div>
      </div>
    </div>
    
    <!-- 悬浮开票按钮 -->
    <div class="floating-billing-btn" v-show="showFloatingBtn" @click="openBillingModal">
      <span>开票 ({{ selectedOrderIds.length }})</span>
    </div>
    
    <!-- 开票模态框 -->
    <div class="modal" v-show="showModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>开具发票</h2>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitBilling">
            <input type="hidden" v-model="orderId">
            
            <div class="form-group">
              <label for="invoiceType">发票类型 <span class="required">*</span></label>
              <select id="invoiceType" v-model="invoiceType" @change="handleInvoiceTypeChange">
                <option value="personal">个人发票</option>
                <option value="company">企业发票</option>
              </select>
            </div>
            
            <div class="form-group" v-show="invoiceType === 'personal'">
              <label for="personalName">姓名 <span class="required">*</span></label>
              <input type="text" id="personalName" v-model="personalName" placeholder="请输入姓名" @input="clearError('personalName')">
              <div class="error-message" v-show="errors.personalName">{{ errors.personalName }}</div>
            </div>
            
            <div class="form-group" v-show="invoiceType === 'personal'">
              <label for="idCardNumber">身份证号码 <span class="required">*</span></label>
              <input type="text" id="idCardNumber" v-model="idCardNumber" placeholder="请输入身份证号码" @input="clearError('idCardNumber')">
              <div class="error-message" v-show="errors.idCardNumber">{{ errors.idCardNumber }}</div>
            </div>
            
            <div class="form-group" v-show="invoiceType === 'company'">
              <label for="companyName">企业名称 <span class="required">*</span></label>
              <input type="text" id="companyName" v-model="companyName" placeholder="请输入企业名称" @input="clearError('companyName')">
              <div class="error-message" v-show="errors.companyName">{{ errors.companyName }}</div>
            </div>
            
            <div class="form-group" v-show="invoiceType === 'company'">
              <label for="taxNumber">税号 <span class="required">*</span></label>
              <input type="text" id="taxNumber" v-model="taxNumber" placeholder="请输入税号" @input="clearError('taxNumber')">
              <div class="error-message" v-show="errors.taxNumber">{{ errors.taxNumber }}</div>
            </div>
            
            <div v-show="invoiceType === 'company'">
              <button class="btn btn-secondary expand-btn" @click="showCompanyOptionalFields = !showCompanyOptionalFields">
                {{ showCompanyOptionalFields ? '收起选填信息' : '展开选填信息' }}
              </button>
              <div v-show="showCompanyOptionalFields">
                <div class="form-group">
                  <label for="address">公司地址</label>
                  <input type="text" id="address" v-model="address" placeholder="请输入公司地址">
                </div>
                
                <div class="form-group">
                  <label for="phone">公司电话</label>
                  <input type="tel" id="phone" v-model="phone" placeholder="请输入公司电话">
                </div>
                
                <div class="form-group">
                  <label for="bank">公司开户行</label>
                  <input type="text" id="bank" v-model="bank" placeholder="请输入公司开户行">
                </div>
                
                <div class="form-group">
                  <label for="account">开户行账号</label>
                  <input type="text" id="account" v-model="account" placeholder="请输入开户行账号">
                </div>
              </div>
            </div>
            

            
            <div class="form-group">
              <label for="email">接收邮箱 <span class="required">*</span></label>
              <input type="email" id="email" v-model="email" placeholder="请输入接收发票的邮箱" @input="clearError('email')">
              <div class="error-message" v-show="errors.email">{{ errors.email }}</div>
            </div>
            
            <div class="form-group">
              <label for="remark">备注</label>
              <textarea id="remark" v-model="remark" placeholder="请输入备注信息"></textarea>
            </div>
          </form>
        </div>
        <div class="modal-bottom">
          <div class="invoice-amount-section">
            <label>开票金额</label>
            <div class="invoice-amount-display">¥{{ invoiceAmount }}</div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeModal">取消</button>
            <button class="btn btn-primary billing-btn" @click="submitBilling">提交开票</button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 成功提示框 -->
    <div class="success-modal" v-show="showSuccessModal">
      <div class="success-modal-content">
        <div class="success-icon">✓</div>
        <h3>{{ successMessage }}</h3>
        <p>我们会尽快处理您的开票申请</p>
        <button class="btn btn-primary" @click="closeSuccessModal">确定</button>
      </div>
    </div>
    
    <!-- 错误提示框 -->
    <div class="error-modal" v-show="showErrorModal">
      <div class="error-modal-content">
        <div class="error-modal-body">
          <p class="error-message-text">{{ errorMessage }}</p>
        </div>
        <div class="error-modal-footer">
          <button class="btn btn-primary" @click="closeErrorModal">确定</button>
        </div>
      </div>
    </div>
    
    <!-- 退出登录确认弹窗 -->
    <div class="logout-modal" v-show="showLogoutModal">
      <div class="logout-modal-content">
        <div class="logout-modal-header">
          <h3>退出登录</h3>
        </div>
        <div class="logout-modal-body">
          <p>确定要退出登录吗？</p>
        </div>
        <div class="logout-modal-footer">
          <button class="btn btn-secondary" @click="closeLogoutModal">取消</button>
          <button class="btn btn-primary" @click="confirmLogout">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getOrderList, invoiceApply, getInvoiceList } from '@/api/api'

export default {
  name: 'OrderBillingPage',
  data() {
    return {
      // 控制企业选填字段的展开/收起toggleView
      showCompanyOptionalFields: false,
      // 订单数据
      orders: [],
      // 开票记录数据
      invoiceRecords: [],
      // 开票记录总数
      totalInvoiceRecords: 0,
      // 分页相关
      currentPage: 1,
      pageSize: 10,
      // 开票记录分页
      currentInvoicePage: 1,
      invoicePageSize: 6,
      // 视图状态
      isInvoiceRecords: false,
      // 选中的订单
      selectedOrderIds: [],
      showFloatingBtn: false,
      // 模态框相关
      showModal: false,
      showSuccessModal: false,
      showErrorModal: false,
      errorMessage: '',
      successMessage:'',
      showLogoutModal: false,
      orderId: '',
      invoiceType: 'personal',
      personalName: '',
      idCardNumber: '',
      companyName: '',
      taxNumber: '',
      address: '',
      phone: '',
      bank: '',
      account: '',
      invoiceAmount: '',
      email: '',
      remark: '',
      // 错误信息
      errors: {
        personalName: '',
        idCardNumber: '',
        companyName: '',
        taxNumber: '',
        email: ''
      },
      // 订单列表相关
      isLoading: false,
      userId: '',
      gameId: '', // 从localStorage获取
      totalOrders: 0
    }
  },
  computed: {
    // 计算当前页的订单
    filteredOrders() {
      const startIndex = (this.currentPage - 1) * this.pageSize
      const endIndex = startIndex + this.pageSize
      return this.orders.slice(startIndex, endIndex)
    },
    // 计算当前页的开票记录
    filteredInvoiceRecords() {
      const startIndex = (this.currentInvoicePage - 1) * this.invoicePageSize
      const endIndex = startIndex + this.invoicePageSize
      return this.invoiceRecords.slice(startIndex, endIndex)
    },
    // 计算总页数
    totalPages() {
      return Math.ceil(this.totalOrders / this.pageSize)
    },
    // 计算开票记录总页数
    totalInvoicePages() {
      return Math.ceil(this.totalInvoiceRecords / this.invoicePageSize)
    }
  },
  mounted() {
    // 从localStorage获取userId（Sdkuid）
    this.userId = localStorage.getItem('userId') || ''
    // 从localStorage获取gameId
    this.gameId = localStorage.getItem('gameId') || ''
    if (this.userId && this.gameId) {
      // 获取订单列表
      this.getOrders()
    }
  },
  methods: {
    // 获取订单列表
    async getOrders() {
      this.isLoading = true
      try {
        const response = await getOrderList({
          gameId: this.gameId,
          userId: this.userId,
          page: this.currentPage,
          limit: this.pageSize
        })
        //console.log('订单列表完整响应:', response)
        
        // 接口返回的数据结构：{data: [...], page: "1", total_page: 1, count: "6"}
        // 直接检查data是否是数组
        if (response && Array.isArray(response.data)) {
          //console.log('原始订单列表:', response.data)
          this.orders = response.data.map(order => ({
              id: order.id, // 数字ID
              orderId: order.orderId, // 订单号
              date: order.time ? new Date(parseInt(order.time) * 1000).toLocaleString('zh-CN') : '',
              amount: parseFloat(order.amount) || 0,
              status: order.is_invoice === '0' ? '未开票' : order.is_invoice === '1' ? '开票中' : '已开票',
              product: order.productName || order.product || '游戏充值',
              quantity: order.quantity || 1,
              customer: order.roleId || order.customer || ''
            }))
         // console.log('处理后的订单列表:', this.orders)
         // console.log('订单数量:', this.orders.length)
          this.totalOrders = parseInt(response.count) || this.orders.length
        } else {
          //console.log('response.data不是数组，值为:', response.data)
          this.orders = []
          this.totalOrders = 0
        }
      } catch (error) {
        //console.error('获取订单失败:', error)
       // console.error('错误详情:', JSON.stringify(error, null, 2))
        this.orders = []
        this.totalOrders = 0
      } finally {
        this.isLoading = false
      }
    },
    // 获取开票记录列表
    async getInvoiceRecords() {
      this.isLoading = true
      try {
        const response = await getInvoiceList({
          gameId: this.gameId,
          userId: this.userId,
          page: this.currentInvoicePage,
          limit: this.invoicePageSize
        })
        //console.log('开票记录列表响应:', response)
        
        // 接口返回的数据结构：{data: [...], page: "1", total_page: 1, count: "3"}
        if (response && Array.isArray(response.data)) {
          //console.log('原始开票记录:', response.data)
          this.invoiceRecords = response.data.map(record => ({
              id: record.id,
              title: record.invoice_title,
              amount: parseFloat(record.invoice_money) || 0,
              status: record.invoice_status === '0' ? '开票中' : record.invoice_status === '1' ? '已开票' : '失败',
              applyTime: record.ctime ? new Date(parseInt(record.ctime) * 1000).toLocaleString('zh-CN') : '',
              type: record.invoice_type === '0' ? '个人发票' : '企业发票',
              orderIds: record.order_ids
            }))
         // console.log('处理后的开票记录:', this.invoiceRecords)
          this.totalInvoiceRecords = parseInt(response.count) || this.invoiceRecords.length
        } else {
          //console.log('response.data不是数组，值为:', response.data)
          this.invoiceRecords = []
          this.totalInvoiceRecords = 0
        }
      } catch (error) {
       // console.error('获取开票记录失败:', error)
        this.invoiceRecords = []
        this.totalInvoiceRecords = 0
      } finally {
        this.isLoading = false
      }
    },
    // 切换视图
    toggleView() {
      this.isInvoiceRecords = !this.isInvoiceRecords
      this.currentPage = 1
      this.currentInvoicePage = 1
      
      // 根据当前视图获取对应数据
      if (this.userId && this.gameId) {
        if (this.isInvoiceRecords) {
          this.getInvoiceRecords()
        } else {
          this.getOrders()
        }
      }
    },
    // 更改页码
    changePage(page) {
      if (this.isInvoiceRecords) {
        const totalPages = this.totalInvoicePages
        if (page < 1 || page > totalPages) return
        this.currentInvoicePage = page
        // 重新获取开票记录
        this.getInvoiceRecords()
      } else {
        const totalPages = this.totalPages
        if (page < 1 || page > totalPages) return
        this.currentPage = page
        // 重新获取订单列表
        this.getOrders()
      }
    },
    // 获取订单状态的样式类
    getStatusClass(status) {
      switch(status) {
        case '未开票':
          return 'status-unbilled'
        case '开票中':
          return 'status-billing'
        case '已开票':
          return 'status-billed'
        default:
          return ''
      }
    },
    // 获取开票记录状态的样式类
    getInvoiceStatusClass(status) {
      switch(status) {
        case '开票中':
          return 'status-billing'
        case '已开票':
          return 'status-billed'
        default:
          return ''
      }
    },
    // 更新悬浮按钮状态
    updateFloatingButton() {
      const checkedBoxes = document.querySelectorAll('.order-checkbox:checked')
      this.selectedOrderIds = Array.from(checkedBoxes).map(box => box.dataset.orderId)
      this.showFloatingBtn = this.selectedOrderIds.length > 0
    },
    // 打开开票模态框
    openBillingModal() {
      if (this.selectedOrderIds.length === 0) {
        return
      }
      
      // 计算总金额，确保只处理未开票的订单
      let totalAmount = 0
      const validOrderIds = []
      this.selectedOrderIds.forEach(orderId => {
        const order = this.orders.find(o => o.id === orderId)
        if (order && order.status === '未开票') {
          totalAmount += order.amount
          validOrderIds.push(orderId)
        }
      })
      
      if (validOrderIds.length === 0) {
        return
      }
      
      // 打开开票模态框，设置总金额
      this.orderId = validOrderIds.join(',')
      this.invoiceAmount = totalAmount
      this.showModal = true
    },
    // 关闭模态框
    closeModal() {
      this.showModal = false
      this.resetForm()
    },
    // 重置表单
    resetForm() {
      this.invoiceType = 'personal'
      this.personalName = ''
      this.idCardNumber = ''
      this.companyName = ''
      this.taxNumber = ''
      this.address = ''
      this.phone = ''
      this.bank = ''
      this.account = ''
      this.invoiceAmount = ''
      this.email = ''
      this.remark = ''
      this.errors = {
        personalName: '',
        idCardNumber: '',
        companyName: '',
        taxNumber: '',
        email: ''
      }
    },
    // 处理发票类型变化
    handleInvoiceTypeChange() {
      // 重置除了invoiceType之外的其他表单字段
      this.personalName = ''
      this.idCardNumber = ''
      this.companyName = ''
      this.taxNumber = ''
      this.address = ''
      this.phone = ''
      this.bank = ''
      this.account = ''
      this.email = ''
      this.remark = ''
      this.errors = {
        personalName: '',
        idCardNumber: '',
        companyName: '',
        taxNumber: '',
        email: ''
      }
    },
    // 清除错误信息
    clearError(field) {
      this.errors[field] = ''
    },
    // 提交开票
    async submitBilling() {
      // 重置错误信息
      this.errors = {
        personalName: '',
        idCardNumber: '',
        companyName: '',
        taxNumber: '',
        email: ''
      }
      
      let hasError = false
      
      // 简单验证
      if (this.invoiceType === 'personal') {
        if (!this.personalName) {
          this.errors.personalName = '请填写姓名'
          hasError = true
        }
        if (!this.idCardNumber) {
          this.errors.idCardNumber = '请填写身份证号码'
          hasError = true
        }
      } else if (this.invoiceType === 'company') {
        if (!this.companyName) {
          this.errors.companyName = '请填写企业名称'
          hasError = true
        }
        if (!this.taxNumber) {
          this.errors.taxNumber = '请填写税号'
          hasError = true
        }
      }
      
      if (!this.email) {
        this.errors.email = '请填写接收邮箱'
        hasError = true
      }
      
      if (!this.invoiceAmount || parseFloat(this.invoiceAmount) <= 0) {
        hasError = true
      }
      
      if (hasError) {
        return
      }
      
      // 调用开票申请接口
      try {
        const requestData = {
          gameId: this.gameId,
          userId: this.userId,
          order_ids: this.orderId, // 多个订单ID用逗号分隔
          invoice_type: this.invoiceType === 'personal' ? '0' : '1', // 0是个人发票，1是公司发票
          invoice_title: this.invoiceType === 'personal' ? this.personalName : this.companyName,
          invoice_money: this.invoiceAmount.toString(),
          email: this.email,
          remark: this.remark
        }
        
        if (this.invoiceType === 'personal') {
          requestData.idCard = this.idCardNumber
        } else if (this.invoiceType === 'company') {
          requestData.company_num = this.taxNumber
          requestData.company_address = this.address
          requestData.company_tel = this.phone
          requestData.company_bank = this.bank
          requestData.company_account_number = this.account
        }
        
        console.log('开票申请数据:', requestData)
        
        const response = await invoiceApply(requestData)
        console.log('开票申请响应:', response)
        
        // 关闭模态框
        this.closeModal()
        
        // 显示成功提示
        this.showSuccessModal = true
        
        // 清空选中的复选框
        document.querySelectorAll('.order-checkbox:checked').forEach(checkbox => {
          checkbox.checked = false
        })
        this.updateFloatingButton()
        
        // 重新获取订单列表
        this.getOrders()
        
      } catch (error) {
        console.error('开票申请失败:', error)
        this.showError('开票申请失败，请稍后重试')
      }
    },
    // 关闭成功提示框
    closeSuccessModal() {
      this.showSuccessModal = false
    },
    
    // 显示错误弹窗
    showError(message) {
      this.errorMessage = message
      this.showErrorModal = true
    },
    
    // 关闭错误弹窗
    closeErrorModal() {
      this.showErrorModal = false
      this.errorMessage = ''
    },
    // 退出登录
    logout() {
      this.showLogoutModal = true
    },
    // 关闭退出登录弹窗
    closeLogoutModal() {
      this.showLogoutModal = false
    },
    // 确认退出登录
    confirmLogout() {
      this.showLogoutModal = false
      localStorage.removeItem('userId')
      localStorage.removeItem('token')
      localStorage.removeItem('gameId')
      this.$router.push('/')
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
  background: #f5f7fa;
  min-height: 100vh;
}

.header {
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header h1 {
  color: #333;
  font-size: 18px;
  font-weight: 600;
}

.header .user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header .user-info span {
  color: #666;
  font-size: 14px;
}

.header .user-info button {
  padding: 6px 12px;
  background: #f0f0f0;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  color: #333;
  cursor: pointer;
  transition: background 0.3s ease;
}

.header .user-info button:hover {
  background: #e0e0e0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.order-list {
  display: grid;
  gap: 16px;
  margin-bottom: 20px;
}

.order-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 12px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.order-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.order-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.order-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.checkbox-placeholder {
  width: 16px;
  height: 16px;
}

.order-header .order-id {
  font-size: 12px;
  color: #666;
}

.order-header .order-status {
  font-size: 12px;
  font-weight: 500;
  color: #52c41a;
}

.order-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.order-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  padding: 2px 0;
}

.order-item .value {
  color: #333;
  font-weight: 500;
}

.btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a6fe0;
}

.btn-secondary {
  background: #f0f0f0;
  color: #333;
}

.btn-secondary:hover {
  background: #e0e0e0;
}

.pagination {
  display: flex;
  justify-content: center;
  margin-top: 40px;
}

.pagination-controls {
  display: flex;
  gap: 8px;
}

.pagination-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.pagination-btn:hover {
  background: #f5f5f5;
}

.pagination-btn.active {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.pagination-btn:disabled {
  background: #f0f0f0;
  color: #999;
  cursor: not-allowed;
}

.floating-billing-btn {
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 18px 36px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  z-index: 99;
  min-width: 200px;
  text-align: center;
}

.floating-billing-btn:hover {
  background: linear-gradient(135deg, #5a6fe0 0%, #6b4192 100%);
  transform: translateX(-50%) translateY(-8px);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.5);
}

/* 红色星号提示 */
.required {
  color: #ff4d4f;
  margin-left: 4px;
}

/* 展开/收起按钮 */
.expand-btn {
  width: 100%;
  margin: 10px 0;
  background: #f0f0f0;
  border: 1px solid #d9d9d9;
  color: #333;
  border-radius: 4px;
  padding: 8px;
  font-size: 12px;
  cursor: pointer;
}

.expand-btn:hover {
  background: #e6f7ff;
  border-color: #91d5ff;
  color: #1890ff;
}

/* 模态框底部固定样式 */
.modal-content {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow-y: hidden;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.modal-bottom {
  border-top: 1px solid #e8e8e8;
  padding: 16px 20px;
  background: #fafafa;
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
}

.invoice-amount-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
 
  padding-bottom: 12px;
  border-bottom: 1px solid #e8e8e8;
}

.invoice-amount-section label {
  font-weight: 500;
  font-size: 14px;
}

.invoice-amount-display {
  width: 150px;
  font-size: 16px;
  font-weight: 600;
  color: red;
  text-align: right;
}

/* 模态框底部按钮样式 */
.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.modal-footer button {
  flex: 1;
  padding: 12px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  text-align: center;
}

/* 提交开票按钮样式 */
.billing-btn {
  background: #667eea;
  color: white;
  border: none;
}

.billing-btn:hover {
  background: #5a6fe0;
  opacity: 0.9;
}

/* 取消按钮样式 */
.btn-secondary {
  background: #f0f0f0;
  color: #333;
  border: 1px solid #d9d9d9;
}

.btn-secondary:hover {
  background: #e6f7ff;
  border-color: #91d5ff;
  color: #1890ff;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  color: #333;
  font-size: 14px;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 80px;
}

.error-message {
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 加载状态 */
.loading-state {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 40px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-state p {
  font-size: 14px;
  color: #999;
}

.empty-state {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 40px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.empty-state img {
  width: 60px;
  height: 60px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.empty-state p {
  font-size: 14px;
  color: #999;
}

.invoice-record-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 12px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.invoice-record-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.invoice-record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.invoice-record-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.invoice-record-status {
  font-size: 12px;
  font-weight: 500;
  color: #52c41a;
}

.invoice-record-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.invoice-record-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  padding: 2px 0;
}

.invoice-record-item .label {
  color: #666;
}

.invoice-record-item .value {
  color: #333;
  font-weight: 500;
}

.status-unbilled {
  color: #ff4d4f;
}

.status-billing {
  color: #faad14;
}

.status-billed {
  color: #52c41a;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .header {
    padding: 12px 16px;
  }
  
  .header h1 {
    font-size: 16px;
  }
  
  .container {
    padding: 16px;
  }
  
  .section-title {
    font-size: 14px;
  }
  
  .order-card,
  .invoice-record-card {
    padding: 10px;
  }
  
  .order-item,
  .invoice-record-item {
    font-size: 11px;
  }
  
  .order-header .order-id {
    font-size: 11px;
  }
  
  .order-header .order-status,
  .invoice-record-status {
    font-size: 11px;
  }
  
  .btn {
    padding: 5px 10px;
    font-size: 11px;
  }
  
  .floating-billing-btn {
    bottom: 30px;
    padding: 16px 32px;
    font-size: 16px;
    min-width: 180px;
  }
  
  .modal-content {
    margin: 0 16px;
    max-height: 85vh;
  }
  
  .modal-header {
    padding: 12px 16px;
  }
  
  .modal-header h2 {
    font-size: 14px;
  }
  
  .modal-body {
    padding: 16px;
  }
  
  .form-group {
    margin-bottom: 12px;
  }
  
  .form-group label {
    font-size: 13px;
    margin-bottom: 6px;
  }
  
  .form-group input,
  .form-group select,
  .form-group textarea {
    padding: 8px 10px;
    font-size: 13px;
  }
  
  .modal-footer {
    padding: 12px 16px;
  }
  
  .invoice-amount-display {
    width: 120px;
    font-size: 14px;
    font-weight: 600;
  }
  
  .pagination-controls {
    gap: 6px;
  }
  
  .pagination-btn {
    padding: 5px 10px;
    font-size: 11px;
  }
}

.success-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.success-modal-content {
  background: white;
  padding: 32px 40px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: successModalFadeIn 0.3s ease;
}

@keyframes successModalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.success-icon {
  width: 60px;
  height: 60px;
  background: #52c41a;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
  font-size: 32px;
  font-weight: bold;
}

.success-modal-content h3 {
  font-size: 20px;
  color: #333;
  margin-bottom: 8px;
}

.success-modal-content p {
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
}

.success-modal-content .btn {
  padding: 10px 32px;
  font-size: 14px;
  min-width: 120px;
}

@media (max-width: 768px) {
  .success-modal-content {
    padding: 24px 32px;
    margin: 0 16px;
  }
  
  .success-icon {
    width: 50px;
    height: 50px;
    font-size: 28px;
    margin-bottom: 16px;
  }
  
  .success-modal-content h3 {
    font-size: 18px;
  }
  
  .success-modal-content p {
    font-size: 13px;
    margin-bottom: 20px;
  }
  
  .success-modal-content .btn {
    padding: 8px 24px;
    font-size: 13px;
  }
}

/* 错误提示框样式 */
.error-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.error-modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: errorModalFadeIn 0.3s ease;
  min-width: 320px;
  max-width: 400px;
}

@keyframes errorModalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.error-modal-body {
  padding: 24px 24px;
  text-align: center;
}

.error-message-text {
  font-size: 16px;
  color: #ff4d4f;
  margin: 0;
  line-height: 1.4;
}

.error-modal-footer {
  padding: 0 24px 24px;
  display: flex;
  justify-content: center;
}

.error-modal-footer .btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.error-modal-footer .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.error-modal-footer .btn-primary {
  background: #667eea;
  color: white;
}

.error-modal-footer .btn-primary:hover {
  background: #5a6fe0;
}

@media (max-width: 768px) {
  .error-modal-content {
    margin: 0 20px;
    min-width: auto;
    max-width: 90%;
  }
  
  .error-modal-body {
    padding: 20px 20px;
  }
  
  .error-message-text {
    font-size: 14px;
  }
  
  .error-modal-footer {
    padding: 0 20px 20px;
  }
  
  .error-modal-footer .btn {
    padding: 8px 16px;
    font-size: 13px;
  }
}

.logout-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.logout-modal-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  animation: logoutModalFadeIn 0.3s ease;
  min-width: 500px;
  max-width: 480px;
}

@keyframes logoutModalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.logout-modal-header {
  padding: 24px 32px 0;
  text-align: center;
}

.logout-modal-header h3 {
  font-size: 20px;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.logout-modal-body {
  padding: 24px 32px;
  text-align: center;
}

.logout-modal-body p {
  font-size: 16px;
  color: #666;
  margin: 0;
  line-height: 1.4;
}

.logout-modal-footer {
  padding: 0 32px 24px;
  display: flex;
  gap: 16px;
}

.logout-modal-footer .btn {
  flex: 1;
  padding: 12px 0;
  font-size: 16px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.logout-modal-footer .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.logout-modal-footer .btn-secondary {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  color: #333;
}

.logout-modal-footer .btn-secondary:hover {
  background: #e8e8e8;
}

.logout-modal-footer .btn-primary {
  background: #1890ff;
  border: 1px solid #1890ff;
  color: white;
}

.logout-modal-footer .btn-primary:hover {
  background: #40a9ff;
  border-color: #40a9ff;
}

@media (max-width: 768px) {
  .logout-modal-content {
    margin: 0 24px;
    min-width: 80%;
    max-width: 95%;
  }
  
  .logout-modal-header {
    padding: 24px 28px 0;
  }
  
  .logout-modal-header h3 {
    font-size: 18px;
  }
  
  .logout-modal-body {
    padding: 24px 28px;
  }
  
  .logout-modal-body p {
    font-size: 15px;
  }
  
  .logout-modal-footer {
    padding: 0 28px 24px;
    gap: 16px;
  }
  
  .logout-modal-footer .btn {
    padding: 12px 0;
    font-size: 15px;
  }
}
</style>